"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { useAuth } from "@/lib/auth-context";
import { authedFetch } from "@/lib/api-fetch";
import { NOTE_VISIT_MIN_MS, kstDateKey } from "@/lib/note/config";

/**
 * R2 방문 추적기 — 루트 레이아웃(src/app/layout.tsx)에 한 번만 붙는다. 화면에 아무것도 그리지 않는다.
 * - 로그인 상태에서만 동작. 페이지(경로)가 바뀌면 처음부터 다시 잰다 (한 페이지 기준).
 * - 화면이 보이면 POST /api/note/visit/start (서버 시각 기록) → 보이는 시간 누적 20초가 되면
 *   POST /api/note/visit/confirm. 탭이 숨겨지면 누적을 멈춘다.
 * - 판정은 서버가 서버 시각으로 한다 (클라이언트 시간 값은 보내지 않는다). 그날 인정되면 더 보내지 않는다.
 */
const DONE_KEY = "note_visit_done"; // 값: `${uid}_${YYYYMMDD}` — 오늘 인정 완료 표시 (1차 중복 방지, 서버도 멱등)

function readDone(): string | null {
  try {
    return localStorage.getItem(DONE_KEY);
  } catch {
    return null;
  }
}
function writeDone(v: string) {
  try {
    localStorage.setItem(DONE_KEY, v);
  } catch {
    /* ignore */
  }
}

export default function NoteVisitTracker() {
  const { firebaseUser } = useAuth();
  const pathname = usePathname();
  const uid = firebaseUser?.uid ?? null;

  useEffect(() => {
    if (!uid) return;
    const doneKey = () => `${uid}_${kstDateKey(Date.now())}`;
    if (readDone() === doneKey()) return;

    let cancelled = false;
    let token: string | null = null;
    let starting = false;
    let confirming = false;
    let accumulated = 0;
    let visibleSince: number | null = null;
    let finished = false;

    const finish = () => {
      finished = true;
      writeDone(doneKey());
      clearInterval(timer);
    };

    const isVisible = () => document.visibilityState === "visible";

    const start = async () => {
      if (starting || token || finished) return;
      starting = true;
      try {
        const res = await authedFetch("/api/note/visit/start", { method: "POST" });
        if (cancelled || !res.ok) return;
        const j = (await res.json()) as { ignored: boolean; token?: string };
        if (j.ignored) return finish();
        token = j.token ?? null;
        // 서버가 시각을 기록한 뒤부터 센다 (서버 경과 ≥ 클라이언트 누적).
        accumulated = 0;
        visibleSince = isVisible() ? Date.now() : null;
      } catch {
        /* 다음 tick 에 재시도 */
      } finally {
        starting = false;
      }
    };

    const confirm = async () => {
      if (confirming || !token || finished) return;
      confirming = true;
      try {
        const res = await authedFetch("/api/note/visit/confirm", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        });
        if (cancelled || !res.ok) return;
        const j = (await res.json()) as { recognized: boolean; reason?: string };
        if (j.recognized || j.reason === "ignored" || j.reason === "event_ended") return finish();
        if (j.reason === "no_start" || j.reason === "bad_token") {
          token = null; // 날짜가 바뀌었거나 다른 탭이 새로 시작 — 처음부터
          accumulated = 0;
        }
        // too_early: 서버 시계 기준 아직 — 다음 tick 에 다시 확인
      } catch {
        /* 다음 tick 에 재시도 */
      } finally {
        confirming = false;
      }
    };

    const tick = () => {
      if (cancelled || finished) return;
      if (!isVisible()) return;
      if (!token) {
        void start();
        return;
      }
      const now = Date.now();
      if (visibleSince !== null) {
        accumulated += now - visibleSince;
      }
      visibleSince = now;
      if (accumulated >= NOTE_VISIT_MIN_MS) void confirm();
    };

    const onVisibility = () => {
      if (isVisible()) {
        visibleSince = Date.now();
        tick();
      } else if (visibleSince !== null) {
        accumulated += Date.now() - visibleSince;
        visibleSince = null;
      }
    };

    const timer = setInterval(tick, 1000);
    document.addEventListener("visibilitychange", onVisibility);
    tick();

    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [uid, pathname]);

  return null;
}
